<template>
  <div>
    <div
      v-if="deal.is_info || inactiveIndefinitely"
      class="text-left sold-out-email-capture"
    >
      <p v-if="deal.is_info" class="info-deal mb-1">
        <b class="font-weight-semi-bold">{{ infoDealQuestionText }} </b>
        Let us know — and make sure you never miss an amazing deal!
      </p>
      <p v-else-if="inactiveIndefinitely" class="indefinitely-inactive mb-1">
        <b class="font-weight-semi-bold">This deal is unavailable (a bummer, we know).</b>
        Don't miss a great deal again — drop your email to get all our latest tools!
      </p>
      <form
        v-if="!isPricingCardEmailSubmitted"
        @submit.prevent="postEmailWaitlistSignUp"
      >
        <label
          :for="emailInputId"
          class="sr-only"
        >
          Email
        </label>
        <input
          :id="emailInputId"
          v-model="emailAddress"
          type="email"
          name="email"
          placeholder="Email"
          class="form-control rounded mb-3"
          required
        >
        <button
          class="btn btn-primary indefinitely-inactive-btn"
          type="submit"
          :disabled="isProcessing || !isEmailAddressValid"
        >
          <slot name="button">
            {{ emailCaptureButtonText }}
          </slot>
        </button>
      </form>
      <div v-if="isPricingCardEmailSubmitted" class="success">
        <img src="~/static/success-tick.svg" alt="">
        <span class="pl-2">
          <b>You're in!</b>  Thanks for signing up.
        </span>
      </div>
    </div>
    <div v-else>
      <div
        v-if="hasPlans"
        class="pdp-buynow-dropdown mb-3 mb-lg-4"
      >

        <PDPPricingSaveBadge
          v-if="pricingSaveBadgeActive"
          :variant="pricingSaveBadgeVariant"
          :original-price="selectedPlan.original_price"
          :price="selectedPlan.price"
          :selectedPriceText="selectedPriceText"
          class="mb-3"
        />

        <!-- strictly percentage-off-pricing code -->
        <div
          v-if="!pricingSaveBadgeActive"
          class="d-flex flex-column align-items-center align-items-lg-start"
        >
          <div :class="{ 'align-items-lg-center d-lg-flex': !displayPlanType }">
            <span
              v-if="percentSavingsText"
              class="pdp-buynow-price pdp-h3 text-brick mr-2"
            >
              {{ percentSavingsText }}
            </span>
            <span class="pdp-buynow-price pdp-h3">
              {{ selectedPriceText }}
            </span>
          </div>
        </div>

        <div
          v-if="percentSavingsText && !pricingSaveBadgeActive"
          class="pdp-buynow-old-price mb-3 text-center text-lg-left"
        >
          {{ selectedPlan.original_price | currencyWithOptionalDecimal }}
        </div>
        <!-- end percentage-off-pricing -->

        <div class="d-flex justify-content-between mb-2">
          <span class="h5 mb-0 select-plan-title">
            Select a plan
          </span>
          <span>
            <a
              class="font-size-md font-weight-normal"
              href="#pricePlans"
            > View plan details </a>
          </span>
        </div>
        <LegacyDropdown
          class="text-left pricing-card"
          :options="dropdownOptions"
          :value="dropdownValue"
          highlight
          @selected="selectPlan"
        >
          <span
            v-if="selectedPlanText"
            :class="{ 'default-plan': !selectedPlan }"
          >
            {{ selectedPlanText }}
            <span v-if="displayPlanType">/{{ planTypeText }}</span>
          </span>

          <span v-else :class="{'default-plan': !selectedPlan}">
            <!-- No new line span intented since we want price/planType without space -->
            {{ dropdownText }}<span v-if="displayPlanType">/{{ planTypeText }}</span>
          </span>
        </LegacyDropdown>
      </div>

      <div
        v-else-if="deal.default_price"
        class="d-flex flex-column align-items-center align-items-lg-start"
      >
        <div>
          <h6
            v-if="isAnnualSubscription"
            class="annual-subscription"
          >
            Annual subscription of
          </h6>
          <h6
            v-else-if="isOneTimePurchase"
            class="one-time-purchase"
          >
            One time purchase of
          </h6>
        </div>

        <PDPPricingSaveBadge
          v-if="pricingSaveBadgeActive"
          :variant="pricingSaveBadgeVariant"
          :original-price="selectedPlan.original_price"
          :price="selectedPlan.price"
          :percentSavings="percentSavings"
          :selectedPriceText="selectedPriceText"
          class="mb-3"
        />

        <div
          v-else
          :class="{ 'align-items-lg-center d-lg-flex': !displayPlanType }"
        >
          <!-- No new line span intented since we want price/planType without space -->
          <span
            v-if="percentSavingsText"
            class="pdp-buynow-price pdp-h3 text-brick mr-2"
          >
            {{ percentSavingsText }}
          </span>
          <span
            class="pdp-buynow-price pdp-h3"
          >
            {{ defaultPriceText }}
          </span>
          <span
            v-if="displayPlanType && !percentSavingsText"
            class="font-size-base"
          >
            /{{ planTypeText }}
          </span>
          <span
            v-else-if="deal.default_original_price && !percentSavingsText"
            class="pdp-buynow-old-price ml-2"
          >
            {{ deal.default_original_price | currencyWithOptionalDecimal }}
          </span>
          <span
            v-if="displayPlanType && deal.default_original_price && !percentSavingsText"
            class="pdp-buynow-old-price ml-2"
          >
            {{ deal.default_original_price | currencyWithOptionalDecimal }}
          </span>
        </div>
      </div>

      <div
        v-if="!pricingSaveBadgeActive && (!hasPlans && percentSavingsText)"
        class="pdp-buynow-old-price mb-3 text-center text-lg-left"
      >
        {{ selectedPlan.original_price | currencyWithOptionalDecimal }}
      </div>

      <div
        v-if="earlyAccessOrLastCall"
        class="pdp-buynow-plus col-12 p-0"
      >
        <div class="d-block w-100 text-center d-lg-flex justify-content-between">
          <a
            v-if="!userHasPlus"
            :href="`/plus/?ref=plus-page-${deal.slug}`"
            class="btn btn-primary pdp-buynow-plus d-inline-flex align-items-center
                  justify-content-center w-100 w-md-auto"
            aria-label="Join AppSumo Plus to buy now"
          >
            {{ deal.is_free ? 'Get for FREE with' : 'Buy now with' }}
            <img
              class="ml-2"
              :src="plusImage"
              width="146"
              height="14"
              alt="AppSumo Plus"
            >
          </a>
          <button
            v-else
            class="btn btn-primary pdp-buynow-btn d-inline-flex align-items-center
                  justify-content-center w-100 w-md-auto"
            :disabled="disabledBuyButton"
            aria-label="Buy now with AppSumo Plus"
            @click="postSignupOrAddToCart()"
            @keydown.enter="postSignupOrAddToCart()"
          >
            {{ deal.is_free ? 'Get for FREE with' : 'Buy now with' }}
            <img
              class="ml-2"
              :src="plusImage"
              width="146"
              height="14"
              alt="AppSumo Plus"
            >
          </button>
        </div>
      </div>

      <div
        v-else
        class="d-flex flex-column flex-lg-row"
      >
        <!-- for deals with clickthrough url, use link instead -->
        <a
          v-if="clickthroughUrl"
          class="btn btn-primary pdp-buynow-btn w-100"
          :href="clickthroughUrl"
        >
          {{ buyButtonText }}
        </a>
        <button
          v-else
          class="btn btn-primary pdp-buynow-btn w-100"
          :disabled="disabledBuyButton"
          @click="postSignupOrAddToCart()"
          @keydown.enter="postSignupOrAddToCart()"
        >
          {{ buyButtonText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import SubmitEmailMixin from '../../mixins/submit-email';
import {
  CART_LOGIN_REDIRECT_EXPERIMENT_NAME,
  COMING_SOON_BANNER_TEXT,
  WAITLIST_URL,
  PDP_SAVE_BADGE_EXPERIMENT_NAME,
} from '../../constants/global';
import PlusImage from '../../../static/images/home/as-plus-2.svg';
import LegacyDropdown from '../ui/LegacyDropdown.vue';
import PDPPricingSaveBadge from './PDPPricingSaveBadge.vue';

export default {
  components: {
    LegacyDropdown,
    PDPPricingSaveBadge,
  },
  mixins: [
    SubmitEmailMixin,
  ],
  props: {
    deal: {
      type: Object,
      required: true,
    },
    plans: {
      type: Array,
      required: false,
      default: () => [],
    },
    buyButton: {
      type: Object,
      required: true,
    },
    isPricingCardEmailSubmitted: {
      type: Boolean,
      required: true,
    },
    bannerDetails: {
      type: Object,
      default: null,
    },
    userHasPlus: {
      type: Boolean,
      default: false,
    },
    displayPlanType: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      plusImage: PlusImage,
      selectedPlan: this.$store.state?.selectedPlan?.plan,
      isDisabled: false,
    };
  },
  computed: {
    pricingSaveBadgeVariant() {
      return this.$store.getters['experiments/getExperimentVariant'](PDP_SAVE_BADGE_EXPERIMENT_NAME);
    },
    pricingSaveBadgeActive() {
      return !!this.pricingSaveBadgeVariant
        && this.pricingSaveBadgeVariant !== 'control'
        && !!this.percentSavings;
    },
    percentSavings() {
      if (
        !Number(this.selectedPlan?.price)
        || !Number(this.selectedPlan?.original_price)
      ) return '';

      const percentSavings = (1 - this.selectedPlan.price / this.selectedPlan.original_price) * 100;
      return Math.round(percentSavings);
    },
    percentSavingsText() {
      return this.percentSavings ? `-${this.percentSavings}%` : '';
    },
    selectedPriceText() {
      if (!this.selectedPlan?.price) return '';

      return this.$options.filters.currencyWithOptionalDecimal(this.selectedPlan.price);
    },
    defaultPriceText() {
      const formattedPrice = this
        .$options
        .filters
        .currencyWithOptionalDecimal(this.deal.default_price);

      if (this.deal.plan_stack_count > 1) {
        return `Starting at ${formattedPrice}`;
      }

      return formattedPrice;
    },
    isAnnualSubscription() {
      return this.deal?.unique_plan_types?.[0]?.[0] === 'Subscription';
    },
    isOneTimePurchase() {
      if (this.selectedPlan.plan_desc === 'One Time Purchase of') {
        return true;
      }

      // when only has one price
      return !this.hasPlans && !!this.deal.default_price;
    },
    emailInputId() {
      return `email-${this.idSuffix}`;
    },
    inactiveIndefinitely() {
      return this.deal.stage_details?.is_inactive_indefinitely || false;
    },
    infoDealQuestionText() {
      return `Want to see ${this.deal.public_name} on AppSumo?`;
    },
    emailCaptureButtonText() {
      return this.deal.is_info ? 'Yes, bring to AppSumo' : 'Stay in the know';
    },
    earlyAccessOrLastCall() {
      return this.buyButton.is_early_access || this.buyButton.is_last_call;
    },
    hasPlans() {
      return this.plans && this.plans.length > 1;
    },
    isComingSoonState() {
      // is_coming_soon will be true if the current user does not have early access, so we want
      //     to handle these cases first since early access behavior should trump coming soon.
      //
      // note: buyButton.is_coming_soon behaves slightly differently than our bannerDetails
      // coming soon, so we're including the banner details check here to match buy button text
      // to our pdp stage banner as a temporary fix

      return !this.earlyAccessOrLastCall
        && (this.bannerDetails?.text === COMING_SOON_BANNER_TEXT || this.buyButton.is_coming_soon);
    },
    disabledBuyButton() {
      if (this.buyButton.is_ended || this.isComingSoonState) {
        return true;
      }
      if (this.hasPlans) {
        return !this.selectedPlan;
      }
      return this.isDisabled;
    },
    buyButtonText() {
      if (this.buyButton.is_draft) {
        return 'Buy now (Draft)';
      }

      if (this.isComingSoonState) {
        return 'Coming soon';
      }

      if (this.buyButton.is_ended) {
        return 'Sold out';
      }

      if (this.buyButton.is_get_now) {
        return this.deal.is_free ? 'Get now for FREE' : 'Get now';
      }

      return 'Buy now';
    },
    clickthroughUrl() {
      return this.deal.clickthrough_url;
    },
    dropdownOptions() {
      return this.plans.map((plan) => {
        const displayText = `${this.getPlanDisplayText(plan)}`;

        const options = {
          id: plan.id,
          value: plan.price,
          display: displayText,
          subText: plan.original_price > 0
            ? this.getOriginalPricePricingCard(plan.original_price)
            : '',
        };

        return options;
      });
    },
    dropdownValue() {
      return this.selectedPlan.price;
    },
    selectedPlanText() {
      return this.getPlanName(this.selectedPlan);
    },
    dropdownText() {
      return this.getPlanDisplayText(this.selectedPlan);
    },
    planTypeText() {
      let textStr = '';

      for (let i = 0; i < this.deal.unique_plan_types.length; i += 1) {
        const [planType, planInterval, intervalCount] = this.deal.unique_plan_types[i];

        if (['One Time Purchase', 'Lifetime Deal'].includes(planType)) {
          textStr += 'lifetime';
        } else if (planType === 'Limited License') {
          textStr += planInterval === 'year' && intervalCount === 1
            ? 'annual'
            : `${intervalCount} ${planInterval}`;
        } else if (planType === 'Subscription') {
          textStr += 'year';
        } else {
          textStr += planType;
        }

        if (i < this.deal.unique_plan_types.length - 1) {
          textStr += ' & ';
        }
      }

      return textStr;
    },
    redirectAnonUserToLogin() {
      return this.$store.getters['experiments/getExperimentVariant'](
        CART_LOGIN_REDIRECT_EXPERIMENT_NAME,
      ) === 'enabled';
    },
    currentUser() {
      return this.$store.state.user;
    },
  },
  methods: {
    // e.g. '1 Code : $39' => ['1 Code ', ' $39']
    getDropdownTextArray(text) {
      if (!text) return null;

      const splitArray = text.split(':');
      return splitArray.length === 2 ? splitArray : null;
    },
    getPlanName(plan) {
      return this.deal.use_licensing ? plan.public_name : this.pluralizeCodeText(plan.codes);
    },
    getPlanDisplayText(plan) {
      const name = this.getPlanName(plan);

      return `${name} : ${this.$options.filters.currencyWithOptionalDecimal(plan.price)}`;
    },
    pluralizeCodeText(code) {
      return `${code} Code${code > 1 ? 's' : ''}`;
    },
    getOriginalPricePricingCard(originalPrice) {
      return `${this.$options.filters.currencyWithOptionalDecimal(originalPrice)}`;
    },
    selectPlan(plan) {
      this.selectedPlan = this.plans.find((p) => p.id === plan.id);
      this.$emit('changePlan', this.selectedPlan);
    },
    async postEmailWaitlistSignUp() {
      if (!this.$_validateEmail(this.emailAddress)) {
        this.$toastr.w('Please use a valid email address.');
      } else {
        const data = this.$_createFormDataWithUtm();
        data.append('deal_slug', this.deal.slug);
        data.append('waitlist_email', this.emailAddress);

        await this.$_sendEmailRequest(WAITLIST_URL, data);
      }
    },
    postAddToCart() {
      this.isDisabled = true;
      const data = new FormData();
      data.append('id', this.deal.id);
      if (this.selectedPlan) {
        data.append('plan_id', this.selectedPlan.id);
        data.append('quantity', this.selectedPlan.codes);
      } else if (this.deal.default_plan) {
        data.append('plan_id', this.deal.default_plan.id);
      }
      if (this.deal.is_free) {
        const quantity = (this.deal.use_licensing) ? 1 : this.deal.default_plan.codes;
        data.append('quantity', quantity);
      }
      axios.post('/cart/add/', data, this.$_formHeaders())
        .then(() => {
          this.isDisabled = false;
          if (window) {
            // if user is not logged in, and experiment is enabled
            if (!this.currentUser?.isAuthenticated && this.redirectAnonUserToLogin) {
              window.location = '/accounts/login/?next=/cart/';
            } else {
              window.location = '/cart/';
            }
          }
        })
        .catch(() => {
          this.$toastr.e('Something has gone wrong with this request. Please try again.');
          this.isDisabled = false;
        });
    },
    async postSignupOrAddToCart() {
      if (this.buyButton.is_ended) {
        await this.postEmailWaitlistSignUp();
      } else {
        this.postAddToCart();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~/styles/dorado/themes/dorado';
@import '../../styles/utils/breakpoints';

.annual-subscription,
.one-time-purchase {
  font-family: Barlow, Verdana, sans-serif;
  margin-bottom: 0;
}

.select-plan-title {
  font-weight: 600;
  line-height: 24px !important;
}

.pdp-buynow-old-price {
  color: $grace;
  font-size: 14px;
  font-weight: 400;
  text-decoration: line-through;
}

.pdp-buynow-dropdown {
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;

  .default-plan {
    color: $gray-300;
  }

  .pdp-buynow-old-price {
    font-size: 16px;
  }
}

.pdp-buynow-dropdown ::v-deep .subtext {
  text-decoration: line-through;
}

.pdp-buynow-price {
  font-weight: 500;
}

.text-brick {
  color: $brick;
}

/* stylelint-disable selector-no-qualifying-type */
a.pdp-buynow-plus,
button.pdp-buynow-btn {
  border-radius: 40px;
  line-height: 24px;
  font-size: 16px;
  font-weight: 700;
  padding: 8px 24px;
}
/* stylelint-enable selector-no-qualifying-type */

/* potential temp styling below */
.btn-primary {
  border: 1px solid #ffbc00;
  background-color: #ffbc00;
  color: #272727;
  padding: 12px 32px;
  font-size: 16px;
}

.btn-primary:hover,
.btn-primary:disabled {
  border-color: #ffcb33;
  background-color: #ffcb33;
  color: #272727;
}

.btn-primary:active,
.btn-primary:focus {
  border-color: #ffcb33 !important;
  background-color: #ffcb33 !important;
  color: #272727 !important;
}

@include media-breakpoint-down(md) {
  .btn-primary,
  .pdp-buynow-btn {
    width: 100%;
  }
}

@include media-breakpoint-up(md) {
  .pdp-buynow-dropdown ::v-deep(.appsumo-dropdown-inner-container) {
    max-height: 320px;
    overflow: auto;
  }
}
</style>
